import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Highlight } from 'react-instantsearch-dom';
import styled from 'styled-components';
import startCase from 'lodash.startcase';
import config from '../../config';
import Table from '../Table/Table';
import TableBody from '../Table/TableBody';
import TableHead from '../Table/TableHead';
import TableRow from '../Table/TableRow';
import Button from '../Button/Button';
// import { ReactComponent as ChevronDown } from '../../assets/images/svg/chevron-down.svg';
import {
  selectProductLocations,
  selectProductLocationsError,
} from '../../store/selectors/productsSelector';
import {
  formatLocationsHeaderData,
  formatLocationsTableRowData,
} from '../../util/locationsTableHelper';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import useGtmHook from '../../util/hooks/useGtmHook';
import { PRICE_ALERT_STARTED } from '../../constants/gtmEvents';
import {
  trackBuyStarted,
  ENTRY_POINTS,
} from '../PriceAlerts/Common/gtmEventHelper';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import {
  getCurrentAccount,
  getIsNotificationOnlyUser,
  getIsReadOnlyUser,
  selectNagDashboard,
} from '../../store/selectors/accountSelectors';
import { isAccountVerified } from '../../constants/nagDashboardContants';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  accountTypeKeys,
  accountTypes,
} from '../../util/enum/api/accountTypes';
import {
  locationsTable,
  locationsTableActions,
  locationsTableBody,
  locationsTableHead,
  locationsTableRow,
} from '../Table/Components/LocationTable';
import {
  mediaBelow,
  mediaBetween,
  mediaUp,
  pxToRem,
  pxToRemMd,
} from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import {
  uFlexColumn,
  uLineClamp,
  uTransition,
} from '../../assets/styles/utility';
import {
  LOCATION_TYPE_DELIVERY,
  PRODUCT_TYPE_FRACTIONAL,
  productLocationTypes,
} from '../../util/enum/api/productTypes';
import { setWizardContent } from '../../util/helpers/wizardHelpers';
import { useSessionStorageState } from '../../util/hooks/useSessionStorageState';
import {
  IS_BUY_FOR_STORAGE_OPEN,
  IS_RESTRICTION_MODAL_OPEN,
} from '../../constants/sessionStorage';
import BasicAccountRestristionsModal from '../Modals/BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';
import ActionRequiredModal from '../Modals/ActionRequiredModal/ActionRequiredModal';
import { HAA } from '../../constants/Brokers';
import {
  useBuyButtonEvent,
  useCrmEvent,
} from '../../util/helpers/crmEventsHelper';
import {
  BUY_BUTTON_TYPES,
  EVENT_TYPES_ENUM,
  SELL_BUTTON_TYPE,
} from '../../util/enum/api/crmEvents';
import useRestrictionHook from '../../util/hooks/useRestrictionHook';
import { isActionRestrictedHelper } from '../../util/helpers/restrictionHelper';
import { restrictionTypes } from '../../util/enum/api/restrictionTypes';
import { ReactComponent as Cash } from '../../assets/images/svg/money.svg';
import { ReactComponent as Card } from '../../assets/images/svg/credit-card.svg';
import Paragraph from '../Paragraph/Paragraph';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { ReactComponent as Bell } from '../../assets/images/svg/bell.svg';
import InfoModal from '../Buy/components/InfoModal';
import VolumeDiscountPriceTiers from '../Buy/VolumeDiscountPriceTiers';
import { InfoTooltip } from '../Tooltip/InfoTooltip';
import PriceAlertInfoModal from './PriceAlertInfoModal';
import ReadOnlyAccessModal from '../PriceAlerts/ReadOnlyAccessModal';

const ProductCardContainer = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const ProductCardContent = styled.div`
  display: grid;
  height: 100%;
  flex: 1;
  grid-template-columns: ${pxToRem(136)} minmax(0, 1fr);
  grid-template-rows: auto 1fr auto;
  grid-column-gap: ${pxToRem(24)};
  padding: ${pxToRem(16)} ${pxToRem(24)} ${pxToRem(16)} ${pxToRem(16)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: ${variables.shadow.boxShadow};
  position: relative;
  // z-index: 2;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-template-columns: ${pxToRemMd(80)} 1fr;
    grid-template-rows: repeat(6, auto);
    padding: ${pxToRemMd(16)} 0;
  }
`;

const ProductCardImageWrap = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 3;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
    padding-left: ${pxToRemMd(16)};
  }
`;

const ProductCardImage = styled.img`
  width: ${pxToRem(136)};
  object-fit: contain;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 100%;
  }
`;

const ProductCardHeader = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 2;
  display: grid;
  grid-template-columns: 1fr ${pxToRem(264)};
  justify-content: space-between;
  margin-bottom: ${pxToRem(24)};

  ${({ isGS }) =>
    isGS &&
    `
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
    `}

  h2 {
    margin-bottom: ${pxToRem(12)};
  }

  .ais-Highlight-highlighted {
    background: #fff1d6;
    font-style: normal;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-column: 2/3;
    grid-row: 1/2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0;
    padding-right: ${pxToRemMd(16)};

    h2 {
      margin-bottom: ${pxToRemMd(10)};
    }
  }
`;

const ProductCardPriceWrap = styled.div`
  ${uFlexColumn};
  text-align: right;
  letter-spacing: 0;
  flex-shrink: 0;
  margin-left: ${pxToRem(16)};
  position: relative;

  ${({ isGS }) =>
    isGS &&
    `
      text-align: left;
      margin-left: 0;
      align-items: flex-start;
      width: 100%;
      gap: 3px;

      > * {
        align-self: flex-start;
        justify-content: flex-start;
        margin: 0;
      }

      > p {
        align-self: flex-start;
        justify-content: flex-start;
      }
    `}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    text-align: left;
    margin-left: 0;
    // flex-direction: row;
    align-items: flex-end;
    margin-top: ${pxToRemMd(7)};
    width: 100%;
  }
`;

const ProductCardPriceLabel = styled.p`
  font-size: ${pxToRem(12)};
  font-weight: 400;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  margin-bottom: ${pxToRem(4)};

  ${({ isGS }) =>
    isGS &&
    `
      font-family: ${variables.family.goldSilver};
      font-weight: 400;
      font-size: 14px;
      line-height: 140%;
      letter-spacing: 0;
      color: ${themeColors.colorTextSecondary};
    `}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(12)};
    marging-bottom: ${pxToRemMd(2)};
  }
`;

const Divider = styled.div`
  width: ${pxToRem(28)};
  height: 1px;
  background-color: ${themeColors.colorTextSecondary};
  margin: ${pxToRem(10)} 0;
  display: flex;
  align-self: flex-end;
  opacity: 0.2;

  ${({ isGS }) => isGS && `align-self: flex-start;`}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 100%;
    margin: ${pxToRemMd(8)} 0;
  }
`;

const ProductCardPriceValue = styled.p`
  font-size: ${pxToRem(18)};
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: center;
  align-self: flex-end;
  svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }

  p {
    font-size: ${pxToRem(14)};
    font-weight: 500;
    margin: 0 ${pxToRem(4)};
    color: ${themeColors.colorTextSecondary};
  }

  ${({ isGS }) =>
    isGS &&
    `
      font-family: ${variables.family.goldSilver};
      font-weight: 500;
      font-size: 18px;
      line-height: 140%;
      letter-spacing: 0;
      color: ${themeColors.colorTextDark};

      div {
        color: ${themeColors.colorTextDark};
      }

      p {
        font-family: ${variables.family.goldSilver};
        font-weight: 500;
        font-size: 16px;
        line-height: 140%;
        letter-spacing: 0;
        color: ${themeColors.colorTextSecondary};
      }

      ${mediaBelow(variables.breakpoints.bpMd)} {
        && {
          font-size: 18px;

          p {
            font-size: 16px;
          }
        }
      }
    `}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(18)};
    line-height: 1.5;
    font-weight: 600;
    margin-left: ${pxToRemMd(4)};
    width: 100%;

    svg {
      width: ${pxToRemMd(22)};
      height: ${pxToRemMd(22)};
    }

    p {
      font-size: ${pxToRemMd(16)};
      font-weight: 500;
      margin: 0 auto 0 ${pxToRem(4)};
      color: ${themeColors.colorTextPrimary};
    }
  }
`;

const ProductCardPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${ProductCardPriceValue} {
    font-size: ${pxToRem(20)};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: row;
    align-items: center;

    ${ProductCardPriceValue} {
      font-size: ${pxToRemMd(14)};
    }

    p {
      width: auto;
      margin-bottom: 0;
    }
  }
`;

const ProductCardDescription = styled.div`
  grid-column: ${({ isGS }) => (isGS ? 1 / 3 : 2 / 3)};
  grid-row: 2 / 3;
  margin-bottom: ${pxToRem(24)};
  line-height: 1.5;
  font-weight: 500;

  span {
    color: ${themeColors.colorTextSecondary};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    // ${uLineClamp};
    margin-top: ${pxToRemMd(15)};
    padding-right: ${pxToRemMd(16)};
    grid-column: 2 / 3;
  }
`;

const ProductCardText = styled.div`
  grid-column: 1 / 2;

  ${({ isGS }) =>
    isGS &&
    `
      width: 100%;
      text-align: left;

      h2 {
        font-family: Onest;
        font-weight: 700;
        font-size: 24px;
        line-height: 30px;
        letter-spacing: 0;
        color: ${themeColors.colorTextDark};
        margin-bottom: 0;

        span {
          color: ${themeColors.colorTextDark};
        }

        ${mediaBelow(variables.breakpoints.bpMd)} {
          font-size: 18px;
          line-height: 24px;
        }
      }
    `}
`;

const PricePerOzPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  box-sizing: border-box;
  height: 26px;
  margin-top: 12px;
  margin-bottom: 3px;
  gap: 10px;
  padding: 6px 10px;
  border: 1px solid ${themeColors.colorTextSecondary};
  border-radius: 6px;
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 16px;
  line-height: 14px;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
  white-space: nowrap;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const ProductCardActions = styled.div`
  grid-column: 1 / 3;
  display: flex;
  justify-content: flex-end;

  ${({ isGS, hasSideIcons, hasTargetButton }) =>
    isGS &&
    `
      ${mediaUp(variables.breakpoints.bpMd)} {
        grid-row: 3 / 4;
      }
      justify-content: flex-start;
      gap: ${pxToRem(8)};

      > * {
        flex: 0 0 auto;
      }

      && > button[data-cy='button-buy-at-target-price'],
      && > button[data-cy='button-buy-product'] {
        flex: 1;
        min-width: 0;
      }

      && > button {
        font-family: ${variables.family.goldSilver};
        font-weight: 600;
      }

      ${mediaBetween(
        variables.breakpoints.bpXl,
        variables.breakpoints.bpXxxl,
      )} {
        display: grid;
        grid-template-columns: ${
          hasSideIcons ? 'auto auto' : '0 0'
        } minmax(0, 1fr);
        grid-template-areas: ${
          hasTargetButton
            ? `'info bell target' 'buy buy buy'`
            : `'info bell buy'`
        };
        align-items: center;
        column-gap: ${pxToRem(8)};
        row-gap: ${hasTargetButton ? pxToRem(8) : 0};

        && > button[data-cy='button-buy-at-target-price'],
        && > button[data-cy='button-buy-product'] {
          flex: unset;
          min-width: 0;
          margin-right: 0;
        }

        > [data-cy='price-alert-info-icon'] {
          grid-area: info;
          margin-right: ${pxToRem(8)};
        }

        > button[data-cy='button-product-side-action'] {
          grid-area: bell;
          margin-right: ${pxToRem(8)};
        }

        > button[data-cy='button-buy-at-target-price'] {
          grid-area: target;
        }

        > button[data-cy='button-buy-product'] {
          grid-area: buy;
        }
      }
    `}

  > button:first-child {
    // margin-right: ${pxToRem(24)};
  }

  > button {
    min-width: 214px;
    display: flex;
    align-items: center;

    ${({ isOpenDropdown }) =>
      isOpenDropdown && `svg {transform: rotate(180deg);}`};

    svg {
      ${uTransition};
      color: inherit;
      width: ${pxToRem(16)};
      height: ${pxToRem(16)};
      margin-right: ${pxToRem(6)};
    }
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    display: grid;
    grid-template-columns: ${({ hasSideIcons }) =>
        hasSideIcons ? 'auto auto' : '0 0'} minmax(0, 1fr);
    grid-template-areas:
      'info bell target'
      'buy buy buy';
    align-items: center;
    column-gap: 0;
    row-gap: ${pxToRemMd(8)};
    padding: 0 ${pxToRemMd(16)};

    > button {
      margin: 0;
      min-width: 0;
    }

    > button[data-cy='button-product-side-action'] {
      grid-area: bell;
      margin-right: ${pxToRemMd(8)};
    }

    > button[data-cy='button-buy-product'] {
      grid-area: buy;
    }

    > button[data-cy='button-buy-at-target-price'] {
      grid-area: target;
    }

    > [data-cy='price-alert-info-icon'] {
      grid-area: info;
      margin-right: ${pxToRemMd(8)};
    }

    > div {
      grid-area: buy;
    }
  }
`;

// const isOpenDropdown = css`
//   svg {
//     transform: rotate(180deg);
//   }
// `;

export const ProductCardDropdown = styled.div`
  overflow: hidden;
  max-height: 0;

  ${({ isOpenDropdown }) => isOpenDropdown && `max-height: 100%;`};
`;

const PriceTiersButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
  margin-top: 20px;

  svg {
    width: 15px;
    height: 15px;
    margin-right: 4px;
  }

  span {
    color: ${themeColors.colorLinkPrimary};
    font-size: 14px;
    font-weight: 500;
  }

  ${({ isGS }) =>
    isGS &&
    `
      align-items: flex-start;

      svg {
        margin-top: 2px;
      }

      span {
        font-family: ${variables.family.goldSilver};
        font-weight: 500;
        font-size: 16px;
        line-height: 140%;
        letter-spacing: 0;
        color: rgba(28, 71, 146, 1);
      }

      ${mediaBelow(variables.breakpoints.bpMd)} {
        margin-bottom: ${pxToRemMd(16)};
      }
    `}
`;

const ProductCardSideButton = styled.button`
  && {
    min-width: 56px;
  }
  width: 56px;
  height: 44px;
  margin-right: ${pxToRem(10)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid
    ${({ $locked }) =>
      $locked ? themeColors.colorDisabledIcon : themeColors.colorButtonPrimary};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorBackgroundPrimary};
  ${uTransition};

  && svg {
    width: 24px;
    height: 24px;
    margin-right: 0;
  }

  ${({ $locked }) =>
    $locked &&
    `
    && svg path {
      fill: ${themeColors.colorDisabledIcon};
    }
  `}
`;

const BellTriggeredWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const BellTriggeredDot = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${themeColors.colorTriggeredDotOverlay};
`;

const BellActiveAlerts = () => (
  <BellTriggeredWrap>
    <Bell />
    <BellTriggeredDot />
  </BellTriggeredWrap>
);

const BellPriceTriggeredBanner = styled.span`
  position: absolute;
  bottom: calc(100% + 18px);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 20px;
  padding: 2px 6px;
  border-radius: ${variables.borderRadius.borderRadiusSm};
  background-color: ${themeColors.colorBestFlag};
  color: ${themeColors.colorWhite};
  font-size: 14px;
  font-weight: 500;
  line-height: 12px;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 6px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid ${themeColors.colorBestFlag};
  }
`;

const BellPriceTriggered = () => {
  const { t } = useTranslation();

  return (
    <BellTriggeredWrap>
      <Bell />
      <BellPriceTriggeredBanner>
        {t('gtt.bell.priceTriggered')}
      </BellPriceTriggeredBanner>
    </BellTriggeredWrap>
  );
};

export const BELL_VARIANTS = {
  DEFAULT: 'default',
  ACTIVE_ALERTS: 'activeAlerts',
  PRICE_TRIGGERED: 'priceTriggered',
};

const BellIcon = ({ variant }) => {
  if (variant === BELL_VARIANTS.PRICE_TRIGGERED) {
    return <BellPriceTriggered />;
  }

  if (variant === BELL_VARIANTS.ACTIVE_ALERTS) {
    return <BellActiveAlerts />;
  }

  return <Bell />;
};

BellIcon.propTypes = {
  variant: PropTypes.oneOf(Object.values(BELL_VARIANTS)),
};

BellIcon.defaultProps = {
  variant: BELL_VARIANTS.DEFAULT,
};

const BuyAtTargetPriceButton = styled(Button)`
  margin-right: ${({ hasSideIcons }) => (hasSideIcons ? pxToRem(10) : 0)};

  && {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-right: 0;
  }
  ${mediaBetween(variables.breakpoints.bpXl, variables.breakpoints.bpXxl)} {
    margin-right: 0;
  }
`;

const PriceAlertInfoWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  height: 44px;
  margin-right: ${pxToRem(8)};

  && svg {
    width: 17px;
    height: 17px;
    fill: rgba(80, 85, 117, 1);
    margin: 0;
  }

  && [role='tooltip'] > div {
    width: max-content;
    max-width: ${pxToRem(300)};
    white-space: nowrap;
    height: 40px;
    box-sizing: border-box;
    font-size: 14px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const BellInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

function ProductCard({
  product,
  isTestProduct,
  isTradingEnabled,
  collapsable,
  priceTiersClickHandler,
  selectProduct,
  onBuyAtTargetPrice,
  hideBuyAtTargetPrice,
  lockBuyAtTargetPrice,
  handlePriceAlert,
  hasAnyPriceAlert,
  side,
  myHoldingsCodes,
  isMyHoldingsChecked,
  allowAccountChangesPermission,
  displayIRAOptionsPermission,
  displayBasicAccountOptionsPermission,
}) {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 768;
  const { ecommerceGtmEvent } = useGtmHook();
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const productLocations = useSelector(
    selectProductLocations(product.ProductCode),
  );
  const settings = useSelector(selectSettings);
  const account = useSelector(getCurrentAccount);
  const isTestAccount = account.IsTestAccount;
  const { RootUrl } = useSelector(selectSettings);
  const productLocationsError = useSelector(selectProductLocationsError);
  const imageSource = config.algoliaSearch.imageCode
    .replace('{product_code}', product.ProductCode)
    .replace('{base_url}', RootUrl);
  const isUtmaAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.UTMA;
  const filterOutDelivery = displayIRAOptionsPermission || isUtmaAccount;
  const [isModalOpen, setIsModalOpen] = useSessionStorageState(
    IS_BUY_FOR_STORAGE_OPEN,
    false,
  );
  const currentAccount = useSelector(getCurrentAccount);
  const isErrAccount = currentAccount?.AccountStatus === 7;
  const isRequiresAttention = currentAccount?.AccountStatus === 4;
  const isHAA = brokerTheme === HAA;
  const isActionRequired = isHAA && (isRequiresAttention || isErrAccount);
  const [isActionRequiredModalOpen, setIsActionRequiredModalOpen] = useState(
    false,
  );
  const isIRAAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.IRA;
  const isBasicAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.Basic;
  const isPendingEquityAccount =
    isIRAAccount && displayBasicAccountOptionsPermission;

  const hasDisabledAction =
    (!allowAccountChangesPermission && !displayBasicAccountOptionsPermission) ||
    (!productLocations.find((location) => location.LocationType === 12) &&
      displayBasicAccountOptionsPermission) ||
    (side === 'sell' && displayBasicAccountOptionsPermission);
  const nagDashboard = useSelector(selectNagDashboard);

  const [
    isBuyRestrictionModalOpen,
    setIsBuyRestrictionModalOpen,
  ] = useSessionStorageState(IS_RESTRICTION_MODAL_OPEN, false);
  const segregatedCaption = settings?.SegregatedCaption;
  const isTriggerOrderActive = settings?.IsTriggerOrderActive;
  const isPriceAlertActive = settings?.IsPriceAlertActive;
  const isVerifiedAccount = isAccountVerified(nagDashboard);
  const isNotificationOnlyUser = useSelector(getIsNotificationOnlyUser);
  // Drives whether the price-alert info/bell icon columns get any width in the
  // stacked product-card action grid — must match the condition gating those
  // icons below, or "Buy at Target Price" and "Buy" go out of sync in width.
  const showPriceAlertIcons =
    isPriceAlertActive &&
    isVerifiedAccount &&
    !isIRAAccount &&
    !isBasicAccount &&
    !isNotificationOnlyUser;

  const isReadOnlyUser = useSelector(getIsReadOnlyUser);
  const [readOnlyModalOpen, setReadOnlyModalOpen] = useState(false);
  // Read-only user with no alerts on this product: bell is visually locked and
  // tapping explains why. With any alert, the bell stays active (view-only).
  const isBellReadOnlyLocked = isReadOnlyUser && !hasAnyPriceAlert;

  const isPostPaidEnabled = settings?.IsPostPaidActive;
  const isPostPaidVisible = !isIRAAccount && isPostPaidEnabled;
  const restrictions = useRestrictionHook();

  const handleEventBuyButtonType = useBuyButtonEvent();
  const handleCrmEvent = useCrmEvent();

  const isRestriction =
    !isPostPaidVisible &&
    isActionRestrictedHelper(restrictions, [
      restrictionTypes.RestrictBuyForDelivery,
      restrictionTypes.DeliveriesLocked,
    ]);
  const isSellRestricted = isActionRestrictedHelper(restrictions, [
    restrictionTypes.RestrictSell,
  ]);

  const handleEventLocationBuyButtonType = () => {
    handleEventBuyButtonType(
      BUY_BUTTON_TYPES.PRODUCT_CARD_VAULT_LOCATION,
      account,
      product?.Code,
    );
  };

  const handleCrmEventLocationSellButtonType = () => {
    handleCrmEvent(account, EVENT_TYPES_ENUM.Sell_OrderStarted, {
      ItemProductCode: product?.Code,
      Source: SELL_BUTTON_TYPE.PRODUCT_CARD,
    });
  };

  const rowData = formatLocationsTableRowData(
    productLocations,
    priceTiersClickHandler,
    product,
    selectProduct,
    side,
    myHoldingsCodes,
    isMyHoldingsChecked,
    allowAccountChangesPermission,
    isTestAccount,
    isTestProduct,
    isTradingEnabled,
    filterOutDelivery,
    displayBasicAccountOptionsPermission,
    setIsModalOpen,
    isBasicAccount,
    isErrAccount,
    isIRAAccount,
    segregatedCaption,
    handleEventLocationBuyButtonType,
    isRestriction,
    handleCrmEventLocationSellButtonType,
    isSellRestricted,
    isActionRequired,
    setIsActionRequiredModalOpen,
  );

  const showHeaderPerOz = productLocations?.some((item) => item.ShowPerOz);
  const showHeaderPerUnit =
    product.ProductType !== PRODUCT_TYPE_FRACTIONAL ||
    (product.ProductType === PRODUCT_TYPE_FRACTIONAL &&
      productLocations.some((item) => item.LocationType === 13));
  const headerData = formatLocationsHeaderData(
    showHeaderPerOz,
    showHeaderPerUnit,
  );

  const { t } = useTranslation();

  const selectBuyForDeliveryProduct = () => {
    setWizardContent('ShortFlow', true);
    const location = productLocations.filter(
      (item) => item.LocationType === LOCATION_TYPE_DELIVERY,
    );
    selectProduct(
      product,
      location[0],
      undefined,
      ENTRY_POINTS.PRODUCT_DIRECT_BUY,
    );
  };

  const arrangeRowData = (rowData) => {
    const indexOfShipToDoor = rowData.findIndex(
      (item) => item.LocationType === 12,
    );
    if (indexOfShipToDoor === -1) return rowData;
    const [item] = rowData.splice(indexOfShipToDoor, 1);
    rowData.unshift(item);
    return rowData;
  };

  const isInstavault =
    isGS &&
    (product.ProductCode === settings.InstavaultProductCodes[0] ||
      product.ProductCode === settings.InstavaultProductCodes[1]);

  const [open, setOpen] = useState(false);
  const [bellInfoOpen, setBellInfoOpen] = useState(false);
  const [bellVariant, setBellVariant] = useState(BELL_VARIANTS.DEFAULT);
  const linkRef = useRef(null);
  const buyButtonRef = useRef(null);
  const bellButtonRef = useRef(null);

  useEffect(() => {
    if (product?.HasActivePriceAlerts && product?.HasNewTriggeredPriceAlerts) {
      setBellVariant(BELL_VARIANTS.PRICE_TRIGGERED);
    } else if (product?.HasNewTriggeredPriceAlerts) {
      setBellVariant(BELL_VARIANTS.PRICE_TRIGGERED);
    } else if (product?.HasActivePriceAlerts) {
      setBellVariant(BELL_VARIANTS.ACTIVE_ALERTS);
    } else {
      setBellVariant(BELL_VARIANTS.DEFAULT);
    }
  }, [product]);

  const getBellTooltipText = (variant) => {
    if (variant === BELL_VARIANTS.PRICE_TRIGGERED) {
      return t('gtt.bell.tooltipPriceTriggered');
    }
    if (variant === BELL_VARIANTS.ACTIVE_ALERTS) {
      return t('gtt.bell.tooltipActive');
    }
    return t('gtt.bell.tooltipDefault');
  };

  const getLocationType = () => {
    if (isInstavault) return 13;
    const vaultLocation = productLocations.find(
      (loc) => loc.LocationType !== LOCATION_TYPE_DELIVERY,
    );
    return vaultLocation?.LocationType || (isGS ? 2 : 1);
  };

  const symbolCode = productLocations[0]?.SymbolCode;

  const primaryLocation = productLocations?.find(
    (loc) => loc.LocationType === getLocationType(),
  );
  const showPerOzPill =
    isGS && showHeaderPerOz && primaryLocation?.PricePerOz != null;

  return (
    <ProductCardContainer>
      <ProductCardContent>
        <ProductCardImageWrap>
          <ProductCardImage src={encodeURI(imageSource)} alt="Product" />
        </ProductCardImageWrap>
        <ProductCardHeader isGS={isGS}>
          <ProductCardText isGS={isGS}>
            <h2>
              <Highlight attribute="Caption" hit={product} />
            </h2>
            {showPerOzPill && (
              <PricePerOzPill>
                {`${formatMoneyNumeral(primaryLocation.PricePerOz)} / oz`}
              </PricePerOzPill>
            )}
          </ProductCardText>
          {isPostPaidVisible && side === 'buy' ? (
            <ProductCardPriceWrap isGS={isGS}>
              {side === 'buy' && (
                <ProductCardPriceLabel isGS={isGS}>
                  {t('buyWizard.startingAtPrice')}
                </ProductCardPriceLabel>
              )}
              <ProductCardPriceValue isGS={isGS}>
                <Cash />
                <Paragraph>{t('buyWizard.cash')}</Paragraph>

                <div>{formatMoneyNumeral(product.ProductPrice)}</div>
              </ProductCardPriceValue>
              <Divider isGS={isGS} />
              <ProductCardPriceValue isGS={isGS}>
                <Card />
                <Paragraph>{t('buyWizard.card')}</Paragraph>

                <div>{formatMoneyNumeral(product.BuyCCPricePerUnit)}</div>
              </ProductCardPriceValue>
              <PriceTiersButton
                isGS={isGS}
                ref={linkRef}
                onClick={() => setOpen(true)}
              >
                <InfoIcon
                  fill={
                    isGS
                      ? 'rgba(173, 177, 199, 1)'
                      : themeColors.colorTextSecondary
                  }
                />{' '}
                <span>{t('buyWizard.volumeDiscountPriceTiers')}</span>
              </PriceTiersButton>
              {!isMobile && (
                <InfoModal
                  open={open}
                  onClose={() => setOpen(false)}
                  anchorRef={linkRef}
                  title="Volume Discount – Price Tiers"
                  subtitle={
                    isInstavault
                      ? ''
                      : `Vault Location – ${startCase(
                          parseEnumType(
                            productLocationTypes,
                            getLocationType(),
                          ),
                        )}`
                  }
                >
                  <VolumeDiscountPriceTiers
                    symbolCode={symbolCode}
                    location={getLocationType()}
                    isPostPaidVisible={isPostPaidVisible}
                  />
                </InfoModal>
              )}
            </ProductCardPriceWrap>
          ) : (
            <ProductCardPriceWrap isGS={isGS}>
              <ProductCardPrice>
                {side === 'buy' && (
                  <ProductCardPriceLabel isGS={isGS}>
                    {t('buyWizard.startingAtPrice')}
                  </ProductCardPriceLabel>
                )}
                <ProductCardPriceValue isGS={isGS}>
                  {formatMoneyNumeral(product.ProductPrice)}
                </ProductCardPriceValue>

                {side === 'buy' && !isMobile && (
                  <InfoModal
                    open={open}
                    onClose={() => setOpen(false)}
                    anchorRef={linkRef}
                    title="Volume Discount – Price Tiers"
                    subtitle={
                      isInstavault
                        ? ''
                        : `Vault Location – ${startCase(
                            parseEnumType(
                              productLocationTypes,
                              getLocationType(),
                            ),
                          )}`
                    }
                  >
                    <VolumeDiscountPriceTiers
                      symbolCode={symbolCode}
                      location={getLocationType()}
                      isPostPaidVisible={isPostPaidVisible}
                    />
                  </InfoModal>
                )}
              </ProductCardPrice>
              {side === 'buy' && (
                <PriceTiersButton
                  isGS={isGS}
                  ref={linkRef}
                  onClick={() => setOpen(true)}
                >
                  <InfoIcon
                    fill={
                      isGS
                        ? 'rgba(173, 177, 199, 1)'
                        : themeColors.colorTextSecondary
                    }
                  />{' '}
                  <span>{t('buyWizard.volumeDiscountPriceTiers')}</span>
                </PriceTiersButton>
              )}
            </ProductCardPriceWrap>
          )}
        </ProductCardHeader>

        {!isGS && (
          <ProductCardDescription isGS={isGS}>
            <Highlight attribute="Description" hit={product} />
          </ProductCardDescription>
        )}

        <ProductCardActions
          isGS={isGS}
          hasSideIcons={showPriceAlertIcons}
          hasTargetButton={isTriggerOrderActive && !hideBuyAtTargetPrice}
        >
          {!collapsable ? null : (
            <>
              {showPriceAlertIcons && (
                <>
                  <PriceAlertInfoWrap data-cy="price-alert-info-icon">
                    {isMobile ? (
                      <BellInfoButton
                        type="button"
                        onClick={() => setBellInfoOpen(true)}
                      >
                        <InfoIcon />
                      </BellInfoButton>
                    ) : (
                      <InfoTooltip
                        trigger="click"
                        content={getBellTooltipText(bellVariant)}
                        placement="left"
                      />
                    )}
                  </PriceAlertInfoWrap>

                  {isMobile && (
                    <PriceAlertInfoModal
                      open={bellInfoOpen}
                      onClose={() => setBellInfoOpen(false)}
                      message={getBellTooltipText(bellVariant)}
                    />
                  )}

                  <ProductCardSideButton
                    ref={bellButtonRef}
                    type="button"
                    data-cy="button-product-side-action"
                    $locked={isBellReadOnlyLocked}
                    onClick={() => {
                      if (isBellReadOnlyLocked) {
                        setReadOnlyModalOpen(true);
                        return;
                      }
                      const isTriggeredVariant =
                        bellVariant === BELL_VARIANTS.PRICE_TRIGGERED;
                      const initialTab = isTriggeredVariant
                        ? 'Triggered'
                        : 'Active';
                      ecommerceGtmEvent(PRICE_ALERT_STARTED, {
                        product_name: product.Caption,
                        weight: product.WeightCaption,
                        current_price: product.ProductPrice,
                        timestamp: new Date().toISOString(),
                      });
                      handlePriceAlert(product, undefined, initialTab);
                    }}
                  >
                    <BellIcon variant={bellVariant} />
                  </ProductCardSideButton>
                </>
              )}

              {/* Buy at Target Price stays visible for every account (gated
                  only by the feature flag) — unlike the price-alert bell above,
                  which is gated on price-alert eligibility. Ineligible accounts
                  are blocked with a popup on click, not by hiding the CTA. */}
              {isTriggerOrderActive && !hideBuyAtTargetPrice && (
                <BuyAtTargetPriceButton
                  height="44px"
                  variant="primaryOutlined"
                  textTransform="none"
                  fontSize="18px"
                  lockStyle={lockBuyAtTargetPrice}
                  onClick={() => onBuyAtTargetPrice(product, productLocations)}
                  data-cy="button-buy-at-target-price"
                  hasSideIcons={showPriceAlertIcons}
                >
                  {t('gtt.common.buyAtTargetPrice')}
                </BuyAtTargetPriceButton>
              )}

              <Button
                height="44px"
                disabled={
                  !isActionRequired &&
                  (!allowAccountChangesPermission ||
                    (isTestAccount && !isTestProduct) ||
                    !isTradingEnabled ||
                    isPendingEquityAccount)
                }
                lockStyle={isActionRequired || hasDisabledAction}
                variant="primary"
                textTransform="none"
                fontSize="18px"
                onClick={() => {
                  if (isActionRequired) {
                    setIsActionRequiredModalOpen(true);
                    return;
                  }
                  if (hasDisabledAction) {
                    setIsBuyRestrictionModalOpen(true);
                    return;
                  }
                  trackBuyStarted({
                    ecommerceGtmEvent,
                    productName: product?.Caption,
                    entryPoint: ENTRY_POINTS.PRODUCT_DIRECT_BUY,
                  });
                  if (displayBasicAccountOptionsPermission) {
                    selectBuyForDeliveryProduct();
                    if (!isRestriction) {
                      handleEventBuyButtonType(
                        BUY_BUTTON_TYPES.PRODUCT_CARD,
                        account,
                        product?.Code,
                      );
                    }
                  } else {
                    selectProduct(
                      product,
                      undefined,
                      undefined,
                      ENTRY_POINTS.PRODUCT_DIRECT_BUY,
                    );
                    handleEventBuyButtonType(
                      BUY_BUTTON_TYPES.PRODUCT_CARD,
                      account,
                      product?.Code,
                    );
                  }
                }}
                data-cy="button-buy-product"
              >
                {t('product.buyBtn')}
              </Button>
              {isMobile && <div ref={buyButtonRef}> </div>}
            </>
          )}
          {!collapsable && (
            <ProductCardDropdown
              // isOpenDropdown={isOpen}
              data-cy="container-product-vault-mobile"
            >
              <Table css={locationsTable} isLocationTableMobile>
                <TableHead css={locationsTableHead} data={headerData} />
                <TableBody css={locationsTableBody}>
                  {!productLocationsError ? (
                    <TableRow
                      css={locationsTableRow}
                      actionButtonsClassName={locationsTableActions}
                      rowData={rowData}
                      mobileDropdown
                      data-cy="list-item-product-vault"
                    />
                  ) : (
                    <span>
                      {t('product.locationsError', {
                        error: productLocationsError,
                      })}
                    </span>
                  )}
                </TableBody>
              </Table>
            </ProductCardDropdown>
          )}
        </ProductCardActions>
      </ProductCardContent>
      <ProductCardDropdown
        // isOpenDropdown={isOpen}
        data-cy="container-product-vault"
      >
        <Table css={locationsTable}>
          <TableHead css={locationsTableHead} data={headerData} />
          <TableBody css={locationsTableBody}>
            {!productLocationsError ? (
              <TableRow
                css={locationsTableRow}
                actionButtonsClassName={locationsTableActions}
                rowData={arrangeRowData(rowData)}
                data-cy="list-item-product-vault"
              />
            ) : (
              <span>
                {t('product.locationsError', { error: productLocationsError })}
              </span>
            )}
          </TableBody>
        </Table>
      </ProductCardDropdown>
      <ActionRequiredModal
        isOpen={isActionRequiredModalOpen}
        close={() => setIsActionRequiredModalOpen(false)}
      />
      <ReadOnlyAccessModal
        open={readOnlyModalOpen}
        onClose={() => setReadOnlyModalOpen(false)}
        openAtTop
      />
      {(isBasicAccount || isBuyRestrictionModalOpen) && (
        <BasicAccountRestristionsModal
          isOpen={isModalOpen || isBuyRestrictionModalOpen}
          close={() => {
            setIsModalOpen(false);
            setIsBuyRestrictionModalOpen(false);
          }}
          title={
            side === 'buy'
              ? t('basicAccountRestrictions.buy.title')
              : t('basicAccountRestrictions.sell.title')
          }
          description={
            side === 'buy'
              ? t('basicAccountRestrictions.buy.description')
              : t('basicAccountRestrictions.sell.description')
          }
        />
      )}
      {isMobile && (
        <InfoModal
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={linkRef}
          parentRef={buyButtonRef}
          title="Volume Discount – Price Tiers"
          subtitle={
            isInstavault
              ? ''
              : `Vault Location – ${startCase(
                  parseEnumType(productLocationTypes, getLocationType()),
                )}`
          }
        >
          <VolumeDiscountPriceTiers
            symbolCode={symbolCode}
            location={getLocationType()}
            isPostPaidVisible={isPostPaidVisible}
          />
        </InfoModal>
      )}
    </ProductCardContainer>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    ProductCode: PropTypes.string,
    ProductPrice: PropTypes.number,
    Description: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductType: PropTypes.string,
    Code: PropTypes.string,
    BuyCCPricePerUnit: PropTypes.number,
    HasActivePriceAlerts: PropTypes.bool,
    HasNewTriggeredPriceAlerts: PropTypes.bool,
  }).isRequired,
  isTestProduct: PropTypes.bool,
  isTradingEnabled: PropTypes.bool,
  collapsable: PropTypes.bool,
  priceTiersClickHandler: PropTypes.func,
  selectProduct: PropTypes.func,
  onBuyAtTargetPrice: PropTypes.func,
  hideBuyAtTargetPrice: PropTypes.bool,
  lockBuyAtTargetPrice: PropTypes.bool,
  handlePriceAlert: PropTypes.func,
  hasAnyPriceAlert: PropTypes.bool,
  side: PropTypes.string,
  myHoldingsCodes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  isMyHoldingsChecked: PropTypes.bool,
  allowAccountChangesPermission: PropTypes.bool,
  displayIRAOptionsPermission: PropTypes.bool,
  displayBasicAccountOptionsPermission: PropTypes.bool,
};

export default memo(ProductCard);
